import { showToast } from '../Common/Toast';
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../utils/supabaseClient';
import { SUPABASE_URL } from '../../utils/env';
import { fmtDate } from '../../utils/dateFormatters';
import { useBrand } from '../../contexts/BrandContext';
import { getRoleDisplayName } from '../../utils/roleUtils';
import { getDefaultPermissions } from '../../utils/employeePermissions';
import {
  UserGroupIcon,
  PlusIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';



// Native <select> popups are drawn by the OS, so they ignore the app's dark
// glassmorphism unless told: color-scheme dark themes the popup itself, and the
// option class themes each row. Without both, the wizard's dropdowns opened as a
// bright white list against a dark form. Same pair QuoteBuilder uses.
const selectOptionClass = 'bg-slate-800 text-gray-100';

const TeamSetupStep = ({ onComplete, onValidationChange }) => {
  const { user } = useUser();
  const { isSmbMate } = useBrand();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [newMember, setNewMember] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'technician'
  });

  // Labels and the two trade-shaped descriptions come from the brand: onboarding is the
  // FIRST screen a new business sees, so "Technician — Complete field work" offered to an
  // accounting firm is the worst possible place for the trade wording to leak.
  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full system access' },
    { value: 'manager', label: 'Manager', description: 'Manage jobs and team' },
    {
      value: 'dispatcher',
      label: getRoleDisplayName('dispatcher'),
      description: isSmbMate ? 'Schedule and assign jobs' : 'Schedule and dispatch jobs'
    },
    {
      value: 'technician',
      label: getRoleDisplayName('technician'),
      description: isSmbMate ? 'Carry out the work' : 'Complete field work'
    },
    { value: 'sales_rep', label: 'Sales Rep', description: 'Create quotes and manage customers' }
  ];

  // Load existing team members
  useEffect(() => {
    loadTeamMembers();
  }, [user?.company_id]);

  const loadTeamMembers = async () => {
    if (!user?.company_id) return;

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          role,
          status,
          first_name,
          last_name,
          email,
          phone,
          created_at,
          profiles (
            avatar_url
          )
        `)
        .eq('company_id', user.company_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTeamMembers(data || []);

    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate team setup
  const validateTeam = () => {
    const warnings = [];
    
    if (teamMembers.length === 1) {
      warnings.push('Consider adding team members to help manage workload');
    }

    const hasManager = teamMembers.some(member => 
      ['owner', 'admin', 'manager'].includes(member.role)
    );
    
    if (!hasManager && teamMembers.length > 1) {
      warnings.push('Consider adding a manager to help coordinate work');
    }

    onValidationChange?.({
      valid: true, // Team setup is always optional
      errors: [],
      warnings
    });

    return true;
  };

  // Validate on team changes
  useEffect(() => {
    if (!loading) {
      validateTeam();
    }
  }, [teamMembers, loading]);

  // Invite team member
  const inviteTeamMember = async () => {
    if (!newMember.first_name.trim() || !newMember.email.trim() || !user?.company_id) return;

    try {
      setInviting(true);

      // Use the canonical admin-invite-user edge function.
      // This avoids client-side supabase.auth.signUp(), which would overwrite the owner's
      // session and log them out mid-wizard. The edge function creates the auth user +
      // users record (status 'pending_invite') + employee record server-side, sends a real
      // invite email, and derives company_id from the caller's token (multi-tenant safe).
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showToast('error', 'You must be logged in to invite team members.');
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-invite-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newMember.email.trim(),
          first_name: newMember.first_name.trim(),
          last_name: newMember.last_name.trim(),
          role: newMember.role,
          phone_number: null,
          // Seed employee_permissions from the role. Without this the invite created no
          // permissions row at all, so the user fell through to a runtime default — which
          // is how an invited technician ended up seeing Marketplace and Customers
          // (operator, 2026-08-29). The row also has to exist for an admin to have
          // anything to edit on the employee screen later.
          //
          // Sent from the CLIENT rather than derived in the edge function on purpose: the
          // role -> permissions map lives in utils/employeePermissions.js, and duplicating
          // it in Deno would create a fourth copy to keep in step. The runtime cap in
          // getEffectivePermissions is what actually enforces the seat tier, so a stale or
          // over-permissive row cannot grant paid features regardless.
          permissions: getDefaultPermissions(newMember.role)
        })
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Error inviting team member:', result);
        showToast('error', result.error || 'Failed to invite team member. Please try again.');
        return;
      }

      // Reload team members so the newly invited member appears
      await loadTeamMembers();

      // Reset form
      setNewMember({
        first_name: '',
        last_name: '',
        email: '',
        role: 'technician'
      });

      showToast('success', 'Team member invited successfully.');

    } catch (error) {
      console.error('Error inviting team member:', error);
      showToast('error', 'Failed to invite team member. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleComplete = () => {
    if (validateTeam()) {
      onComplete?.({ teamMembers });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 [color-scheme:dark]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading team members...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <UserGroupIcon className="w-12 h-12 text-purple-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Team Setup</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Add your team members so you can assign work and track progress. You can always add more later.
        </p>
      </div>

      {/* Current Team Members */}
      {teamMembers.length > 0 && (
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Current Team</h3>
          <div className="space-y-3">
            {teamMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-white/20 rounded-md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {member.first_name} {member.last_name}
                      {member.id === user.id && <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">(You)</span>}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {member.role.replace('_', ' ')} • {member.status}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {fmtDate(member.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite New Team Member */}
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Invite Team Member</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={newMember.first_name}
              onChange={(e) => setNewMember(prev => ({ ...prev, first_name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="John"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={newMember.last_name}
              onChange={(e) => setNewMember(prev => ({ ...prev, last_name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <EnvelopeIcon className="w-4 h-4 inline mr-1" />
              Email Address *
            </label>
            <input
              type="email"
              value={newMember.email}
              onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Role
            </label>
            <select
              value={newMember.role}
              onChange={(e) => setNewMember(prev => ({ ...prev, role: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-purple-500 [color-scheme:dark]"
            >
              {roles.map(role => (
                <option className={selectOptionClass} key={role.value} value={role.value}>
                  {role.label} - {role.description}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={inviteTeamMember}
            disabled={!newMember.first_name.trim() || !newMember.email.trim() || inviting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {inviting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Inviting...
              </>
            ) : (
              <>
                <PlusIcon className="w-4 h-4" />
                Invite Team Member
              </>
            )}
          </button>
        </div>

        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <p className="text-sm text-blue-800">
            Team members receive an email invitation to set their own password and join your company.
          </p>
        </div>
      </div>

      {/* Skip Option */}
      <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Working Solo?</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          No problem! You can add team members later as your business grows. 
          For now, you'll handle all the work orders yourself.
        </p>
      </div>

      {/* Continue Button */}
      <div className="text-center">
        <button
          onClick={handleComplete}
          className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700"
        >
          <CheckCircleIcon className="w-5 h-5" />
          Continue with {teamMembers.length} Team Member{teamMembers.length !== 1 ? 's' : ''}
        </button>
      </div>
    </div>
  );
};

export default TeamSetupStep;


